import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import CourseCard from '../../components/course/CourseCard';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useStateContext } from '../../hooks/useStateContext';
import useSEO from '../../hooks/useSEO';
import { schemaGraph, bundleProductSchema, courseListSchema } from '../../utils/schema';
// Build-time catalog snapshot so the course grid appears in prerendered HTML
// (react-snap has no API access at build). Client refreshes from /api/courses on load.
import coursesSnapshot from '../../data/coursesSnapshot.json';
const SNAP_COURSES = coursesSnapshot.courses || [];

// Same categories the AZ CoursesPage used; kept per-state so we can extend easily
const CREDIT_TYPES = [
  { value: '', label: 'All Categories' },
  { value: 'core', label: 'Core' },
  { value: 'elective', label: 'Elective' },
  { value: 'agency', label: 'Agency' },
  { value: 'commissioners_standard', label: "Commissioner's Standard" },
  { value: 'contract_law', label: 'Contract Law' },
  { value: 'disclosure', label: 'Disclosure' },
  { value: 'fair_housing', label: 'Fair Housing' },
  { value: 'real_estate_legal_issues', label: 'RE Legal Issues' },
  { value: 'general', label: 'General' },
];

export default function StateCoursesPage() {
  const { state } = useStateContext();
  const { user } = useAuth();
  const { addBundle } = useCart();
  const navigate = useNavigate();
  const [allCourses, setAllCourses] = useState(SNAP_COURSES.filter(c => c.state === state.code));
  const [enrolled, setEnrolled] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  // Start false, NOT true. react-snap prerenders this page in its post-effect state
  // (loading already false, courses shown). If the client's first render returned the
  // loading spinner instead, React would hit a hydration mismatch (#418/#423) and re-render
  // the whole page — the visible "flash". We have seeded course data, so render it immediately.
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [addingBundle, setAddingBundle] = useState(false);
  const [bundleMsg, setBundleMsg] = useState('');

  useSEO({
    title: `${state.name} CE Courses | Steady Education`,
    description: `Browse ${state.regulator}-approved ${state.name} real estate continuing education courses. Self-paced online learning, instant certificates, starting at $${state.coursePrice}.`,
    url: `https://steadyeducation.com/${state.slug}/courses`,
    jsonLd: schemaGraph({
      // The package Product, plus every course in this state's catalogue as a Course list.
      // allCourses is seeded from the build-time snapshot, so this is present in the
      // prerendered HTML rather than appearing only after the API call resolves.
      mainSchemas: [bundleProductSchema(state), courseListSchema(allCourses, state)],
      breadcrumb: [
        { name: 'Home', url: 'https://steadyeducation.com' },
        { name: state.name, url: `https://steadyeducation.com/${state.slug}` },
        { name: 'Courses', url: `https://steadyeducation.com/${state.slug}/courses` },
      ],
    }),
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [coursesRes, enrolledRes] = await Promise.all([
          api.get('/courses'),
          user ? api.get('/users/me/courses') : Promise.resolve({ data: { courses: [] } }),
        ]);
        // Keep the seeded snapshot during prerender/SPA-fallback (HTML response →
        // .courses undefined); only replace it when the real JSON API answers.
        if (Array.isArray(coursesRes?.data?.courses)) {
          setAllCourses(coursesRes.data.courses.filter(c => c.state === state.code));
        }
        setEnrolled((enrolledRes?.data?.courses || []).map(c => c.course_id));
        setEnrolledCourses(enrolledRes?.data?.courses || []);
      } catch (err) {
        console.warn('State courses load failed:', err?.message);
      } finally { setLoading(false); }
    };
    fetchData();
  }, [state.code, user]);

  const statusByCourseId = {};
  enrolledCourses.forEach(c => { statusByCourseId[c.course_id] = c.status; });

  const handleBuyBundle = async () => {
    setAddingBundle(true);
    setBundleMsg('');
    try {
      await addBundle(state.code);
      navigate('/cart');
    } catch (err) {
      setBundleMsg(err.response?.data?.error || 'Could not add bundle');
    } finally { setAddingBundle(false); }
  };

  const filtered = allCourses.filter(c => {
    const matchesFilter = !filter || c.credit_type === filter;
    const matchesSearch = !search || c.title.toLowerCase().includes(search.toLowerCase()) ||
      c.short_description?.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const fullPrice = allCourses.reduce((s, c) => s + parseFloat(c.price || 0), 0);
  const ownsAllCourses = allCourses.length > 0 && allCourses.every(c => enrolled.includes(c.id));
  const allCompleted = ownsAllCourses && allCourses.every(c => statusByCourseId[c.id] === 'completed');
  const showBundle = (!ownsAllCourses || allCompleted) && allCourses.length > 0;

  if (loading) return <div className="loading-wrapper"><div className="spinner" /></div>;

  return (
    <div className="page-wrapper">
      <div className="container">
        <div style={styles.pageHeader}>
          <h1 className="page-title">{state.name} CE Courses</h1>
          <p className="page-subtitle">{state.regulator}-approved continuing education for {state.name} real estate licensees</p>
        </div>

        {allCourses.length === 0 && (
          <div style={styles.comingSoon}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🚀</div>
            <h2 style={styles.comingSoonTitle}>{state.name} courses launching soon</h2>
            <p style={styles.comingSoonDesc}>
              We're finalizing {state.regulator} approval. Reach out to be notified when {state.name} courses go live.
            </p>
            <a href="mailto:help@steadyeducation.com" className="btn btn-primary">Notify Me</a>
          </div>
        )}

        {showBundle && (
          <div style={styles.bundleBanner}>
            <div style={styles.bundleLeft}>
              <div style={styles.bundleTag}>⭐ Best Value</div>
              <h2 style={styles.bundleTitle}>Complete {state.name} CE Package</h2>
              <p style={styles.bundleDesc}>
                All {allCourses.length} {state.regulator}-approved courses — everything you need to complete your {state.requiredHours}-hour {state.name} CE renewal.
              </p>
            </div>
            <div style={styles.bundleRight}>
              <div style={styles.bundleWas}>Individual total: ${fullPrice.toFixed(2)}</div>
              <div style={styles.bundlePrice}>${state.packagePrice}</div>
              <button style={styles.bundleBtn} onClick={handleBuyBundle} disabled={addingBundle}>
                {addingBundle ? 'Adding...' : `Buy ${state.name} Package — $${state.packagePrice}`}
              </button>
              {bundleMsg && <div style={styles.bundleMsg}>{bundleMsg}</div>}
              <div style={styles.bundleNote}>🔒 Secure checkout via Stripe</div>
            </div>
          </div>
        )}

        {allCourses.length > 0 && (
          <>
            <div style={styles.filters}>
              <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
                <input className="input" style={{ paddingLeft: 12, fontSize: 13 }} placeholder="Search courses..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <div style={styles.filterTabs}>
                {CREDIT_TYPES.filter(t => !t.value || !state.creditTypes || state.creditTypes.includes(t.value)).map(t => (
                  <button key={t.value} style={{ ...styles.filterTab, ...(filter === t.value ? styles.filterTabActive : {}) }} onClick={() => setFilter(t.value)}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <h3>No courses found</h3>
                <p style={{ color: 'var(--color-text-muted)' }}>Try adjusting your search or filter</p>
              </div>
            ) : (
              <div style={styles.grid}>
                {filtered.map(c => <CourseCard key={c.id} course={c} owned={enrolled.includes(c.id)} status={statusByCourseId[c.id]} compact />)}
              </div>
            )}
          </>
        )}

        <div style={styles.adreBox}>
          <div style={{ fontSize: 24, flexShrink: 0 }}>🏛️</div>
          <div>
            <strong>All courses approved by the {state.regulatorFull}</strong>
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
              School License {state.schoolLicense} · help@steadyeducation.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  pageHeader: { textAlign: 'center', marginBottom: 32 },
  comingSoon: { textAlign: 'center', padding: '60px 40px', background: 'var(--tan-50)', border: '2px dashed #b39e89', borderRadius: 20, marginBottom: 40 },
  comingSoonTitle: { fontFamily: 'var(--font-display)', fontSize: 26, color: 'var(--forest-700)', marginBottom: 12 },
  comingSoonDesc: { fontSize: 16, color: 'var(--slate-600)', lineHeight: 1.7, maxWidth: 500, margin: '0 auto 24px' },
  bundleBanner: { background: 'linear-gradient(135deg, #1a3a3a 0%, #1d4142 100%)', borderRadius: 20, padding: '36px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 40, flexWrap: 'wrap', marginBottom: 40, boxShadow: '0 8px 32px rgba(29,65,66,0.2)' },
  bundleLeft: { flex: 1, minWidth: 280 },
  bundleTag: { display: 'inline-block', background: 'rgba(179,158,137,0.2)', color: '#d4b896', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, marginBottom: 12, border: '1px solid rgba(179,158,137,0.3)' },
  bundleTitle: { fontFamily: 'var(--font-display)', fontSize: 24, color: 'white', marginBottom: 10, lineHeight: 1.2 },
  bundleDesc: { fontSize: 14, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 },
  bundleRight: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, minWidth: 220 },
  bundleWas: { fontSize: 13, color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through' },
  bundlePrice: { fontSize: 48, fontWeight: 800, color: 'white', lineHeight: 1 },
  bundleBtn: { background: '#b39e89', color: 'white', border: 'none', borderRadius: 10, padding: '14px 28px', fontSize: 15, fontWeight: 800, cursor: 'pointer', width: '100%' },
  bundleMsg: { fontSize: 13, fontWeight: 600, color: '#d4b896', textAlign: 'center' },
  bundleNote: { fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'center' },
  filters: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 },
  filterTabs: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  filterTab: { padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 500, border: '1.5px solid #b39e89', background: 'white', color: 'var(--forest-600)', cursor: 'pointer' },
  filterTabActive: { background: 'var(--forest-500)', color: 'white', borderColor: 'var(--forest-500)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, marginBottom: 40 },
  adreBox: { display: 'flex', alignItems: 'flex-start', gap: 14, background: 'var(--tan-50)', border: '1px solid #b39e89', borderRadius: 12, padding: '16px 20px' },
};
